import { useQuery } from '@tanstack/react-query'
import { apiFetch, hasLiveApi } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import {
  fixtureEventsForPll,
  namesFromGetEventsForPll,
  type GetEventsForPllResponse,
} from '@/api/map/plStatement'
import * as api from '@/data/api'
import { keys } from '@/api/hooks/shared/keys'

export type EventsForPllQuery = {
  range: { from: string; to: string }
  game?: string
  subGame?: string
  memberIds?: string[]
  tms?: string
  name?: { _id: string; accessLevel?: string }
}

function eventsBody(query: EventsForPllQuery): Record<string, unknown> {
  const body: Record<string, unknown> = {
    fromDate: query.range.from.slice(0, 10),
    toDate: query.range.to.slice(0, 10),
  }
  if (query.game && query.game !== 'all') body.game = query.game
  if (query.subGame) body.subGame = query.subGame
  if (query.memberIds?.length) body.playerName = query.memberIds
  if (query.tms) body.tms = query.tms
  if (query.name?._id) body.name = query.name
  return body
}

async function loadEventsForPll(query: EventsForPllQuery): Promise<string[]> {
  if (!hasLiveApi()) {
    const summary = await api.getPnl({
      range: query.range,
      page: 1,
      pageSize: 100_000,
      ...(query.memberIds?.length ? { memberIds: query.memberIds } : {}),
    })
    return fixtureEventsForPll(summary.rows, { subGame: query.subGame })
  }
  try {
    const response = await apiFetch<GetEventsForPllResponse>('/v1/account/getEventsForPLL', {
      method: 'POST',
      body: JSON.stringify(eventsBody(query)),
    })
    return namesFromGetEventsForPll(response)
  } catch (error) {
    if (isUnreachableApiError(error)) return []
    throw error
  }
}

/**
 * Lab `Account/getEventsForPLL` — distinct event names in the P&L window.
 *
 * Reloads when the date range, product, sub-game, or member scope changes,
 * matching Lab `getEvents(formData)` after Sub Game / dates.
 */
export function useEventsForPll(query: EventsForPllQuery) {
  return useQuery({
    queryKey: keys.eventsForPll({
      from: query.range.from.slice(0, 10),
      to: query.range.to.slice(0, 10),
      ...(query.game ? { game: query.game } : {}),
      ...(query.subGame ? { subGame: query.subGame } : {}),
      ...(query.memberIds?.length ? { memberIds: query.memberIds } : {}),
      ...(query.tms ? { tms: query.tms } : {}),
      ...(query.name?._id ? { nameId: query.name._id } : {}),
    }),
    queryFn: () => loadEventsForPll(query),
    staleTime: 30_000,
  })
}
